import React from 'react';
import NavBar from '../components/NavBar';
import GameCard from '../components/GameCard';
import StoreCTA from '../components/StoreCTA';
import WeeklyPackSignup from '../components/WeeklyPackSignup';
import CrownIcon from '../components/CrownIcon';
import './Home.css';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getGameOfTheDayIndex } from '../utils/getGameOfTheDay';
import { useClientDate } from '../utils/useClientDate';

// Game data
const games = [
  {
    title: 'Daily Logic Puzzles',
    description: 'A fresh set of bite-sized logic puzzles to keep your brain sharp. Come back daily for a new challenge!',
    imageUrl: '/dailylogicpuzzles/icons/Icon-512.png',
    link: '/play/dailylogicpuzzles',
    infoLink: '/games/daily-logic-puzzles'
  },
  {
    title: 'Crowns',
    description: 'Test your spatial reasoning and pattern recognition skills in this Sudoku-style crown placement puzzle!',
    imageUrl: '/crowns/icons/Icon-512.png',
    link: '/play/crowns',
    infoLink: '/games/crowns'
  },
  {
    title: 'Binary Sudoku+',
    description: 'A challenging puzzle game where you create perfect symmetry by placing tiles strategically on the board. Test your spatial reasoning and pattern recognition skills!',
    imageUrl: '/binarysudokuplus/icons/Icon-512.png',
    link: '/play/binarysudoku',
    infoLink: '/games/binary-sudoku-plus'
  },
  {
    title: 'Aquarium',
    description: 'Fill the aquariums with the right amount of water in this unique logic puzzle. Use the numbers as clues to determine the water levels in each container.',
    imageUrl: '/aquarium/icons/Icon-512.png',
    link: '/play/aquarium',
    infoLink: '/games/aquarium'
  },
  {
    title: 'Color Flood: Logic Puzzle',
    description: 'A colorful puzzle game where you connect matching colors while filling the entire board. Create beautiful patterns while exercising your logical thinking!',
    imageUrl: '/palettepath/icons/Icon-512.png',
    link: '/play/palettepath',
    infoLink: '/games/palette-path'
  },
  {
    title: 'Dotstream',
    description: 'A new and exciting game where you connect dots to create patterns and solve puzzles. Test your problem-solving skills and creativity!',
    imageUrl: '/dotstream/icons/Icon-512.png',
    link: '/play/dotstream',
    infoLink: '/games/dot-stream'
  },
  {
    title: 'Solitaire Pro Collection',
    description: 'A polished collection of classic solitaire games: Klondike, FreeCell, Spider, TriPeaks, Pyramid, and Golf.',
    imageUrl: '/solitaireprocollection/icons/Icon-512.png',
    link: '/play/solitaireprocollection',
    infoLink: '/games/solitaire-pro-collection'
  },
  {
    title: 'Nonograms',
    description: 'Reveal hidden pictures by filling cells using row and column number clues. Also known as picture logic or paint-by-number logic puzzles.',
    imageUrl: '/nonograms/icons/Icon-512.png',
    link: '/play/nonograms',
    infoLink: '/games/nonograms'
  },
  {
    title: 'Calcudoku',
    description: 'Combine arithmetic with logic in this math puzzle. Fill the grid so each row and column contains every number, while satisfying cage constraints.',
    imageUrl: '/calcudoku/icons/Icon-512.png',
    link: '/play/calcudoku',
    infoLink: '/games/calcudoku'
  },
  {
    title: 'Word Wheel',
    description: 'Find as many words as you can in 60 seconds! Swipe across letters on the wheel to build words.',
    imageUrl: '/wordwheel/icons/Icon-512.png',
    link: '/play/wordwheel',
    infoLink: '/games/word-wheel'
  },
  {
    title: 'Sudoku',
    description: 'The classic number puzzle. Fill the 9x9 grid so that each column, each row, and each of the nine 3x3 subgrids contain all of the digits from 1 to 9.',
    imageUrl: '/sudoku/icons/Icon-512.png',
    link: '/play/sudoku',
    infoLink: '/games/sudoku'
  },
  {
    title: 'Mine Finder',
    description: 'A no-guess mine-finding logic puzzle! Use number clues to deduce where mines are hidden and clear the board without detonating any.',
    imageUrl: '/minefinder/icons/Icon-512.png',
    link: '/play/minefinder',
    infoLink: '/games/mine-finder'
  },
  {
    title: 'Tent Logic',
    description: 'Place tents next to trees on the grid using row and column clues. Each tree gets exactly one tent, and no two tents touch - even diagonally!',
    imageUrl: '/tents/icons/Icon-512.png',
    link: '/play/tents',
    infoLink: '/games/tent-logic'
  },
  {
    title: 'Color Links',
    description: 'Connect matching colors to fill the grid! Draw paths between pairs of colored dots without crossing lines or leaving empty cells.',
    imageUrl: '/colorlinks/icons/Icon-512.png',
    link: '/play/colorlinks',
    infoLink: '/games/color-links'
  },
  {
    title: 'Word Ladder',
    description: 'Climb the puzzle one letter at a time! Change one letter per step to transform the starting word into the target word.',
    imageUrl: '/wordladder/icons/Icon-512.png',
    link: '/play/wordladder',
    infoLink: '/games/word-ladder'
  },
  {
    title: 'Nurikabe',
    description: 'Shade cells to form a connected sea while isolating numbered islands. Each number tells you the size of its island - no 2×2 sea blocks allowed!',
    imageUrl: '/nurikabe/icons/Icon-512.png',
    link: '/play/nurikabe',
    infoLink: '/games/nurikabe'
  },
  {
    title: 'Fleet',
    description: 'Find the hidden fleet! Use row and column clues to deduce where ships are placed. Ships cannot touch each other, not even diagonally.',
    imageUrl: '/fleet/icons/Icon-512.png',
    link: '/play/fleet',
    infoLink: '/games/fleet'
  },
  {
    title: 'Light Up',
    description: 'Place light bulbs to illuminate every cell on the board. No two bulbs can shine on each other, and numbered walls tell you exactly how many bulbs touch them.',
    imageUrl: '/lightup/icons/Icon-512.png',
    link: '/play/lightup',
    infoLink: '/games/light-up'
  },
  {
    title: 'Cryptograms',
    description: 'Decode famous quotes by cracking the substitution cipher. Each letter stands for another - figure out the pattern to reveal the hidden message!',
    imageUrl: '/cryptograms/icons/Icon-512.png',
    link: '/play/cryptograms',
    infoLink: '/games/cryptograms'
  },
  {
    title: 'Futoshiki',
    description: 'Fill the grid with numbers following greater-than and less-than clues between cells. Each row and column contains every number exactly once.',
    imageUrl: '/futoshiki/icons/Icon-512.png',
    link: '/play/futoshiki',
    infoLink: '/games/futoshiki'
  },
  {
    title: 'Galaxies',
    description: 'Divide the grid into regions, each with perfect 180-degree rotational symmetry around its center dot. A beautiful spatial reasoning challenge!',
    imageUrl: '/galaxies/icons/Icon-512.png',
    link: '/play/galaxies',
    infoLink: '/games/galaxies'
  },
  {
    title: 'Kurodoku',
    description: 'Shade cells black or white using number clues that show how many white cells are visible in each direction. Keep all white cells connected!',
    imageUrl: '/kurodoku/icons/Icon-512.png',
    link: '/play/kurodoku',
    infoLink: '/games/kurodoku'
  },
  {
    title: 'Logic Grid Puzzles',
    description: 'Classic zebra-puzzle riddles! Use clues to figure out which items match across multiple categories using an elimination grid.',
    imageUrl: '/logicgridpuzzles/icons/Icon-512.png',
    link: '/play/logicgridpuzzles',
    infoLink: '/games/logic-grid-puzzles'
  },
  {
    title: 'Mosaic',
    description: 'Fill cells so each number counts the filled cells in its 3x3 neighborhood, and a hidden picture appears. Like Mine Finder meets picture puzzles!',
    imageUrl: '/mosaic/icons/Icon-512.png',
    link: '/play/mosaic',
    infoLink: '/games/mosaic'
  },
  {
    title: 'Pipes',
    description: 'Rotate pipe pieces to connect them all into one network with no loose ends. A classic connection puzzle that tests your spatial reasoning!',
    imageUrl: '/pipes/icons/Icon-512.png',
    link: '/play/pipes',
    infoLink: '/games/pipes'
  },
  {
    title: 'Renzoku',
    description: 'Fill the grid with numbers where dots between cells mean those numbers are consecutive. No dot means they are not. Pure logical deduction!',
    imageUrl: '/renzoku/icons/Icon-512.png',
    link: '/play/renzoku',
    infoLink: '/games/renzoku'
  },
  {
    title: 'Slant',
    description: 'Place a diagonal line in every cell. Numbers at intersections tell you how many lines meet there. No closed loops allowed!',
    imageUrl: '/slant/icons/Icon-512.png',
    link: '/play/slant',
    infoLink: '/games/slant'
  },
  {
    title: 'Yin-Yang',
    description: 'Fill every cell with a black or white circle. Both colors must form one connected group, and no 2x2 area can be all the same color.',
    imageUrl: '/yinyang/icons/Icon-512.png',
    link: '/play/yinyang',
    infoLink: '/games/yin-yang'
  },
  {
    title: 'Skyscrapers',
    description: 'Place buildings of varying heights so each row and column contains every height exactly once. Use visibility clues from the edges to determine placement!',
    imageUrl: '/skyscrapers/icons/Icon-512.png',
    link: '/play/skyscrapers',
    infoLink: '/games/skyscrapers'
  },
  {
    title: 'Thermometers',
    description: 'Fill thermometers to the right level using row and column clues. Each thermometer fills from its bulb - figure out how much mercury to add!',
    imageUrl: '/thermometers/icons/Icon-512.png',
    link: '/play/thermometers',
    infoLink: '/games/thermometers'
  },
  {
    title: 'Domino Puzzle',
    description: 'Place every domino so each colored region keeps its rule, or split a number grid into a full set of dominoes. Three new daily puzzles every day!',
    imageUrl: '/dominosa/icons/Icon-512.png',
    link: '/play/dominosa',
    infoLink: '/games/dominosa'
  },
  {
    title: 'NoriNori',
    description: 'Shade exactly two cells in each region to form dominoes. No two shaded areas can touch - even diagonally!',
    imageUrl: '/norinori/icons/Icon-512.png',
    link: '/play/norinori',
    infoLink: '/games/norinori'
  },
  {
    title: 'LITS',
    description: 'Place one tetromino (L, I, T, or S) in each region. Shaded cells must connect, but no 2x2 block can be fully shaded and identical shapes can\'t touch!',
    imageUrl: '/lits/icons/Icon-512.png',
    link: '/play/lits',
    infoLink: '/games/lits'
  },
];

// Featured on the home page; everything else is listed compactly below the grid.
const FEATURED = ['Crowns', 'Daily Logic Puzzles', 'Binary Sudoku+', 'Sudoku', 'Nonograms', 'Calcudoku', 'Mine Finder', 'Aquarium', 'Tent Logic', 'Logic Grid Puzzles', 'Cryptograms', 'Word Wheel'];
const featured = FEATURED.map((t) => games.find((g) => g.title === t)).filter(Boolean);
const more = games.filter((g) => !FEATURED.includes(g.title));

const CATEGORIES = [
  { title: 'Daily Crowns', meta: 'A new puzzle every day', to: '/crowns-puzzle', icon: <CrownIcon /> },
  { title: 'Logic puzzles', meta: '25+ games', to: '/logic-games', icon: '◆' },
  { title: 'Word games', meta: 'Word Wheel, Final Phrase…', to: '/word-games', icon: 'Aa' },
  { title: 'Card games', meta: 'Solitaire, Euchre', to: '/card-games', icon: '♠' },
  { title: 'Printable', meta: 'PDF puzzle packs', to: '/printable', icon: '⎙' },
];

function Home() {

  // Build-day game first (matches the prerendered HTML), today's game after mount.
  const today = useClientDate();
  const gameOfTheDayIndex = getGameOfTheDayIndex(games.length, today);
  const gameOfTheDay = games[gameOfTheDayIndex];

  return (
    <div>
      <Helmet>
        <title>Logic Puzzles - Play 30+ Free Logic Puzzles & Brain Games Online</title>
        <meta name="description" content="Play free online logic puzzles: Sudoku, Nonograms, Calcudoku, Mine Finder, Crowns, and 30+ more brain games. No signup, no downloads - every puzzle solvable by pure deduction. Start solving now!" />
        <link rel="canonical" href="https://logicpuzzles.ca" />
        <meta property="og:title" content="Logic Puzzles - Play 30+ Free Logic Puzzles & Brain Games Online" />
        <meta property="og:description" content="Play 30+ free logic puzzles in your browser: Sudoku, Nonograms, Calcudoku, Mine Finder, Crowns and more. No signup needed." />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta property="og:url" content="https://logicpuzzles.ca" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Logic Puzzles - Play 30+ Free Logic Puzzles & Brain Games Online" />
        <meta name="twitter:description" content="Play 30+ free logic puzzles in your browser: Sudoku, Nonograms, Calcudoku, Mine Finder, Crowns and more. No signup needed." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
        <link rel="preload" as="image" href={gameOfTheDay.imageUrl} />
      </Helmet>
      <NavBar />

      <section className="home-hero">
        <div className="container home-hero__grid">
          <div className="home-hero__copy">
            <p className="eyebrow">Free logic puzzles · No signup</p>
            <h1>
              Logic puzzles, <em>beautifully</em> made.
            </h1>
            <p className="home-hero__lead">
              Play {games.length}+ hand-crafted puzzles in your browser: Crowns, Sudoku, Nonograms, Calcudoku and more.
              Every one is solvable by pure logic, with no guessing.
            </p>
            <div className="home-hero__actions">
              <Link to="/crowns-puzzle" className="button spark"><CrownIcon /> Play today’s Crowns</Link>
              <Link to="/logic-games" className="button secondary">Browse all games</Link>
            </div>
            <ul className="home-hero__facts">
              <li><strong>{games.length}+</strong> puzzle types</li>
              <li><strong>200K+</strong> app downloads</li>
              <li><strong>4.8★</strong> Crowns on the App Store</li>
            </ul>
          </div>

          <div className="gotd" suppressHydrationWarning>
            <p className="gotd__label">★ Game of the day</p>
            <Link to={gameOfTheDay.link} className="gotd__card" onClick={() => {
              if (typeof gtag !== 'undefined') {
                gtag('event', 'game_of_the_day_click', {
                  'game_name': gameOfTheDay.title,
                  'event_category': 'engagement',
                  'event_label': 'game_of_the_day'
                });
              }
            }}>
              <img
                src={gameOfTheDay.imageUrl}
                alt={`${gameOfTheDay.title} - Game of the Day`}
                loading="eager"
                fetchpriority="high"
                width="160"
                height="160"
              />
              <h2>{gameOfTheDay.title}</h2>
              <p>{gameOfTheDay.description}</p>
              <span className="button">Play now →</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="home-categories-wrap" aria-label="Browse by category">
        <div className="container home-categories">
        {CATEGORIES.map((c) => (
          <Link key={c.to} to={c.to} className="category-tile">
            <span className="category-tile__icon" aria-hidden="true">{c.icon}</span>
            <span>
              <strong>{c.title}</strong>
              <small>{c.meta}</small>
            </span>
          </Link>
        ))}
        </div>
      </section>

      <section className="games-section">
        <div className="container">
        <div className="home-section-head">
          <div>
            <p className="eyebrow">Featured</p>
            <h2>Popular puzzles</h2>
          </div>
          <Link to="/logic-games" className="home-link">All logic games →</Link>
        </div>
        <div className="games-grid">
          {featured.map((game, index) => (
            <GameCard
              key={game.title}
              title={game.title}
              description={game.description}
              imageUrl={game.imageUrl}
              link={game.link}
              infoLink={game.infoLink}
              loading={index < 3 ? "eager" : "lazy"}
              priority={index === 0}
            />
          ))}
        </div>
        <h3 className="home-more-title">More puzzles to explore</h3>
        <ul className="home-more">
          {more.map((game) => (
            <li key={game.title}>
              <Link to={game.infoLink || game.link}>
                <img src={game.imageUrl.replace('/icons/Icon-512.png', '/icons/Icon-192.png')} alt="" width="28" height="28" loading="lazy" />
                {game.title}
              </Link>
            </li>
          ))}
        </ul>
        </div>
      </section>

      <section className="home-why">
        <div className="container">
          <div className="home-section-head">
            <div>
              <p className="eyebrow">Why LogicPuzzles.ca</p>
              <h2>What makes our puzzles different</h2>
            </div>
          </div>
          <p className="section-intro">
            LogicPuzzles.ca focuses on clarity, fairness, and satisfying “aha” moments. Every puzzle is designed to be solvable
            using pure logic - no guessing, no tricks.
          </p>
          <div className="home-why__grid">
            <div className="highlight-grid">
              <div className="highlight-card">
                <h3>Hand-Crafted Logic</h3>
                <p>Each puzzle goes through multiple design passes so the rules stay clean and the deductions feel earned.</p>
              </div>
              <div className="highlight-card">
                <h3>Unique Solutions</h3>
                <p>We validate puzzles to ensure a single, consistent solution whenever the rules say there should be one.</p>
              </div>
              <div className="highlight-card">
                <h3>Mobile-Ready Play</h3>
                <p>Touch-friendly grids, fast loading, and clean UI make the web experience as smooth as the apps.</p>
              </div>
            </div>
            <div className="home-process">
              <h3 className="home-process__title">How we build a great puzzle</h3>
              <ol className="process-grid">
                <li className="process-step">
                  <h4>Design the core rule</h4>
                  <p>We start with a simple rule set and test for clear deductions, then build puzzle templates around it.</p>
                </li>
                <li className="process-step">
                  <h4>Balance difficulty</h4>
                  <p>We calibrate early levels for easy onboarding and ramp up to expert challenges with layered logic.</p>
                </li>
                <li className="process-step">
                  <h4>Verify &amp; polish</h4>
                  <p>Puzzles are checked for unique solutions, visual clarity, and snappy performance before release.</p>
                </li>
              </ol>
            </div>
          </div>
          <div className="home-progression">
            <h3>Skill levels for every solver</h3>
            <p>
              New to logic puzzles? Start with bite-sized daily challenges. Ready for deeper deductions? Dive into Crowns,
              Aquarium, or Binary Sudoku+ for expert-level reasoning. Each game includes a variety of grid sizes and difficulty
              settings so you can grow at your own pace.
            </p>
          </div>
        </div>
      </section>

      <section className="trust">
        <div className="container">
          <div className="trust__head">
            <p className="eyebrow">Trusted by puzzle fans</p>
            <h2>Loved on the App Store and Google Play</h2>
            <p>
              Logic Puzzles games have been downloaded over 200,000 times, and Crowns holds a 4.8-star average on the App Store.
              Thousands of puzzles and years of design feedback power the same polished gameplay you can now enjoy in the browser.
            </p>
          </div>
          <div className="trust-grid">
            <div className="trust-card">
              <h3>200K+</h3>
              <p>App downloads</p>
            </div>
            <div className="trust-card">
              <h3>4.8 ★</h3>
              <p>Crowns App Store rating</p>
            </div>
            <div className="trust-card">
              <h3>{games.length}+</h3>
              <p>Free puzzle types</p>
            </div>
          </div>
          <div className="review-grid">
            <figure className="review-card">
              <blockquote>
                “This game is a lot of fun if you enjoy this style of puzzle. It's an easy game to pick up and play for 5 minutes and walk away from, or you can get caught up in it and spend hours playing levels.”
              </blockquote>
              <figcaption>Google Play review</figcaption>
            </figure>
            <figure className="review-card">
              <blockquote>
                “I absolutely love Crowns! It’s the perfect mix of logic and strategy, keeping my mind engaged while being incredibly fun to play.”
              </blockquote>
              <figcaption>App Store review</figcaption>
            </figure>
            <figure className="review-card">
              <blockquote>
                “This game is super fun and well made. Love the conveniences like auto-place x and swipe to mark. I find myself bouncing between game modes, which keeps the game feeling fresh.”
              </blockquote>
              <figcaption>App Store review</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="home-platforms">
        <div className="container">
          <div className="home-section-head">
            <div>
              <p className="eyebrow">Our apps</p>
              <h2>Play on web or mobile</h2>
            </div>
            <Link to="/apps" className="home-link">See all our apps →</Link>
          </div>
          <p className="section-intro">
            Prefer the app experience? Our flagship puzzles are free on iPhone, iPad and Android, with offline play,
            daily puzzles, hints and streaks.
          </p>
          <div className="home-platforms__grid">
            <StoreCTA app="crowns" placement="home" page="/" headline="Crowns: the crown-placing logic puzzle" />
            <StoreCTA app="dlp" placement="home" page="/" headline="Daily Logic Puzzles: eight new puzzles every day" />
            <StoreCTA app="bsp" placement="home" page="/" headline="Binary Sudoku+: 1,300+ binary puzzles" />
          </div>
        </div>
      </section>

      <section className="home-faq">
        <div className="container home-faq__grid">
          <div className="welcome">
            <p className="eyebrow">About this site</p>
            <h2>Welcome to LogicPuzzles.ca</h2>
            <p>
              Dive into our collection of engaging puzzle games designed to challenge your mind and provide hours of entertainment.
              Each game offers a unique experience, from spatial reasoning to pattern recognition, all optimized for both desktop
              and mobile play.
            </p>
            <p>
              Our games are crafted with care, featuring intuitive controls and progressive difficulty levels to keep you engaged
              and challenged. Whether you're a casual player or a puzzle enthusiast, there's something here for everyone.
            </p>
            <Link to="/contact" className="home-link">Ask a question →</Link>
          </div>
          <div>
            <h2>Frequently asked questions</h2>
            <div className="home-faq-list">
              <details className="home-faq-item" open>
                <summary>Are the puzzles free to play?</summary>
                <p>Yes. All web puzzles are free, and you can unlock extra features on mobile if you want to support the games.</p>
              </details>
              <details className="home-faq-item">
                <summary>Do puzzles repeat?</summary>
                <p>No. Our daily puzzles rotate and our larger collections are built to give you thousands of unique boards.</p>
              </details>
              <details className="home-faq-item">
                <summary>Is this the same content as the apps?</summary>
                <p>The web experience mirrors the apps, including the same game rules, polished UI, and difficulty tiers.</p>
              </details>
            </div>
          </div>
        </div>
      </section>

      <section id="subscribe" className="subscribe">
        <div className="container">
          <WeeklyPackSignup source="home" variant="inline" />
        </div>
      </section>
    </div>
  );
}

export default Home;

